import type { RootNodesForDimensionsResponse } from '@amzn/gft-dimsum-service';
import { KPI_DIMENSION_CONFIG_MAP, KPI_DIMENSION_RENDER_LIST } from '@constants/page';
import { KpiFormulaNode, KpiFormulaNodeType } from '@deckManagement/types/CalculatedKPIBuilder.types';
import { KPIDrivers } from '@services/api/deckManagement/graphql/schema/API';
import { isEmpty, isNil, isString } from 'lodash';
import { parse } from 'mathjs';
import { PartialKPIDrivers } from 'types/Page.types';
import { BaseKpiDimensionFieldTypes } from 'types/PageSidePanel.types';

const MATH_OPERATORS = '+-*/';
const MATH_SYMBOLS = `${MATH_OPERATORS}()`;

/**
 * Determines if a dimension value needs to be quoted when placed in a per-dimension formula string.
 * Values containing spaces or math operator characters are ambiguous without quotes.
 * @param value - The raw dimension value
 */
const needsQuoting = (value: string): boolean => /[\s+\-*/()]/.test(value);

/**
 * Wraps a dimension value in double quotes if it contains characters that would be
 * ambiguous in a per-dimension formula string (spaces, math operators).
 * @param value - The raw dimension value
 */
export const quoteIfNeeded = (value: string): string => (needsQuoting(value) ? `"${value}"` : value);

/**
 * Helper function to get dimension key from formula key using KPI_DIMENSION_CONFIG_MAP
 * @param formulaKey - The key found in kpi_formula string (e.g., 'Account', 'REVGP')
 * @returns The dimension key used across Auriga (e.g., 'account', 'revgp') or null if not found
 */
const getDimensionKeyFromFormulaKey = (formulaKey: string): string | null => {
  const configEntry = Object.values(KPI_DIMENSION_CONFIG_MAP).find((config) => config.formulaKey === formulaKey);
  return configEntry?.key || null;
};

/**
 * Helper function to get formula key from dimension key using KPI_DIMENSION_CONFIG_MAP
 * @param dimensionKey - The dimension key used across Auriga (e.g., 'account', 'revgp')
 * @returns The formula key found in kpi_formula string (e.g., 'Account', 'REVGP') or null if not found
 */
const getFormulaKeyFromDimensionKey = (dimensionKey: string): string | null => {
  const configEntry = Object.values(KPI_DIMENSION_CONFIG_MAP).find((config) => config.key === dimensionKey);
  return configEntry?.formulaKey || null;
};

/**
 * The entry object for a ParseResult.
 * @see ParseResult
 */
interface ParseResultEntry {
  isFormula: boolean;
  value: string | null;
}

/**
 * The result of a parsed formula
 */
export interface ParseResult {
  [key: string]: ParseResultEntry;
}

/**
 * The result of a generated formula. Includes KPI ID and Formula
 */
interface GenerateFormulaResult {
  id: string;
  formula: string;
}

/**
 * Merges in the pageDrivers to the ParseResult object.
 *
 * NOTE: Formula objects directly defined from parsing a formula override any object generated from pageDrivers.
 * @param formulaData
 * @param pageDrivers
 */
export const mergePageDriversToParsedFormula = (
  formulaData: ParseResult,
  pageDrivers: PartialKPIDrivers | KPIDrivers,
): ParseResult => {
  const pageDriverFormulaData: ParseResult = {};
  Object.keys(pageDrivers).forEach((key) => {
    const dimensionKey = key as keyof PartialKPIDrivers;
    const formulaKey = getFormulaKeyFromDimensionKey(key);
    if (formulaKey) {
      pageDriverFormulaData[dimensionKey] = {
        isFormula: false,
        value: pageDrivers[dimensionKey]!,
      };
    }
  });

  return {
    ...pageDriverFormulaData,
    ...formulaData,
  };
};

/**
 * Finds the closing pipe `|` index for a segment starting after the opening pipe.
 * A segment ends at the `|` whose next non-space character is an operator, paren, a segment, or end-of-string.
 * @param formula - The raw formula string
 * @param startIndex - Index to start searching from (after the opening `|`)
 * @returns Index of the closing `|`
 */
const findSegmentEnd = (formula: string, startIndex: number): number => {
  let charIndex = startIndex;
  const formulaLength = formula.length;
  while (charIndex < formulaLength) {
    if (formula[charIndex] === '|') {
      let nextCharIndex = charIndex + 1;
      // Skip blank spaces to find the next meaningful character after this pipe
      while (nextCharIndex < formulaLength && formula[nextCharIndex] === ' ') {
        nextCharIndex += 1;
      }

      if (
        nextCharIndex >= formulaLength ||
        MATH_SYMBOLS.includes(formula[nextCharIndex]) ||
        formula[nextCharIndex] === '|'
      ) {
        // This pipe is the segment's closing delimiter — the next meaningful character
        // is an operator, paren, another segment, or end-of-string.
        return charIndex;
      }
    }
    charIndex += 1;
  }
  return charIndex;
};

/**
 * Splits raw formula into dimension-level formulas/entities.
 *
 * Uses a tokenizer that walks the string character-by-character to correctly handle
 * dimension values containing math operators (e.g., "Units Served - Paid Plus Hedge").
 * Pipe characters `|` delimit segments; operators are only recognized outside segments.
 *
 * @param rawFormula
 * @param pageDrivers
 */
export const splitRawFormula = (rawFormula: string, pageDrivers?: PartialKPIDrivers) => {
  const result: ParseResult = {};
  if (isEmpty(rawFormula)) return result;

  // Step 1: Tokenize — walk the string to produce segment and operator tokens.
  // Segments are delimited by |...|, operators are +,-,*,/ outside segments.
  const tokens: { type: 'segment' | 'operator' | 'paren'; value: string }[] = [];
  let cursor = 0;
  const formulaLength = rawFormula.length;

  while (cursor < formulaLength) {
    const char = rawFormula[cursor];

    if (char === ' ') {
      // Skip space between tokens
      cursor += 1;
    } else if (char === '|') {
      // Segment: starts with |
      const segmentEnd = findSegmentEnd(rawFormula, cursor + 1);
      tokens.push({ type: 'segment', value: rawFormula.substring(cursor, segmentEnd + 1) });
      cursor = segmentEnd + 1;
    } else if (char === '(' || char === ')') {
      // Parentheses
      tokens.push({ type: 'paren', value: char });
      cursor += 1;
    } else if (MATH_OPERATORS.includes(char)) {
      // Operators
      tokens.push({ type: 'operator', value: char });
      cursor += 1;
    } else {
      // Skip any other unexpected characters
      cursor += 1;
    }
  }

  // Step 2: Extract dimension key:value pairs from each segment token
  const dimensionFormulas: { [key: string]: string[] } = {};

  tokens.forEach((token) => {
    if (token.type !== 'segment') return;
    // Strip outer pipes: |Key:Val|Key:Val| → Key:Val|Key:Val
    const inner = token.value.slice(1, -1);
    const pairs = inner.split('|');
    pairs.forEach((pair) => {
      const colonIdx = pair.indexOf(':');
      if (colonIdx > 0) {
        const key = pair.substring(0, colonIdx);
        const value = pair.substring(colonIdx + 1);
        const mappedKey = getDimensionKeyFromFormulaKey(key);
        if (mappedKey) {
          if (!dimensionFormulas[mappedKey]) dimensionFormulas[mappedKey] = [];
          dimensionFormulas[mappedKey].push(value);
        }
      }
    });
  });

  // Step 3: Build operators list (only operators between/before segments, not inside parens used as grouping)
  // Reconstruct the formula value for dimensions with multiple unique values
  const segmentIndices: number[] = [];
  tokens.forEach((token, tokenIdx) => {
    if (token.type === 'segment') segmentIndices.push(tokenIdx);
  });

  // Determine if formula starts with a unary negative
  const startsWithNegative = tokens.length > 0 && tokens[0].type === 'operator' && tokens[0].value === '-';

  // Collect operators between segments (accounting for leading unary minus)
  const operators: string[] = [];
  if (startsWithNegative) {
    operators.push('-');
  }
  for (let segIdx = 1; segIdx < segmentIndices.length; segIdx += 1) {
    // Find operator token(s) between consecutive segments
    const prevSegmentEnd = segmentIndices[segIdx - 1];
    const currSegmentStart = segmentIndices[segIdx];
    for (let tokenIdx = prevSegmentEnd + 1; tokenIdx < currSegmentStart; tokenIdx += 1) {
      if (tokens[tokenIdx].type === 'operator') {
        operators.push(tokens[tokenIdx].value);
        break; // Take first operator between segments
      }
    }
  }

  // Check if formula contains parentheses
  const hasParentheses = tokens.some((token) => token.type === 'paren');

  // Remove parentheses that wrap a single segment when they serve as trivial grouping
  // (i.e., preceded by operator/start and followed by operator/end).
  // Keep parens that represent implicit multiplication like "segment (segment)".
  if (hasParentheses) {
    let tokenIdx = 0;
    while (tokenIdx < tokens.length - 2) {
      if (
        tokens[tokenIdx].type === 'paren' &&
        tokens[tokenIdx].value === '(' &&
        tokens[tokenIdx + 1].type === 'segment' &&
        tokens[tokenIdx + 2].type === 'paren' &&
        tokens[tokenIdx + 2].value === ')'
      ) {
        const beforeIsOperatorOrStart = tokenIdx === 0 || tokens[tokenIdx - 1].type === 'operator';
        const afterIsOperatorOrEnd = tokenIdx + 3 >= tokens.length || tokens[tokenIdx + 3].type === 'operator';

        if (beforeIsOperatorOrStart && afterIsOperatorOrEnd) {
          tokens.splice(tokenIdx + 2, 1); // remove closing paren
          tokens.splice(tokenIdx, 1); // remove opening paren
        } else {
          tokenIdx += 1;
        }
      } else {
        tokenIdx += 1;
      }
    }
  }

  const hasRemainingParentheses = tokens.some((token) => token.type === 'paren');

  // Step 4: Build ParseResult
  Object.keys(dimensionFormulas).forEach((key) => {
    const values = dimensionFormulas[key];
    const uniqueValues = [...new Set(values)];

    if (uniqueValues.length === 1) {
      result[key] = { isFormula: false, value: uniqueValues[0] };
    } else if (hasRemainingParentheses) {
      // Reconstruct formula preserving parentheses structure
      let formulaValue = '';

      tokens.forEach((token) => {
        if (token.type === 'operator') {
          formulaValue += (formulaValue ? ' ' : '') + token.value;
        } else if (token.type === 'paren') {
          if (token.value === '(') {
            // No space before ( if it follows an operator, attach directly
            formulaValue += `${formulaValue && !formulaValue.endsWith(' ') ? ' ' : ''}(`;
          } else {
            formulaValue += ')';
          }
        } else if (token.type === 'segment') {
          // Extract this dimension's value from the segment
          const inner = token.value.slice(1, -1);
          const pairs = inner.split('|');
          let dimValue = '';
          pairs.forEach((pair) => {
            const colonIdx = pair.indexOf(':');
            if (colonIdx > 0) {
              const pairKey = pair.substring(0, colonIdx);
              const mappedKey = getDimensionKeyFromFormulaKey(pairKey);
              if (mappedKey === key) {
                dimValue = quoteIfNeeded(pair.substring(colonIdx + 1));
              }
            }
          });
          // No space after opening paren
          if (formulaValue.endsWith('(')) {
            formulaValue += dimValue;
          } else {
            formulaValue += (formulaValue ? ' ' : '') + dimValue;
          }
        }
      });

      result[key] = { isFormula: true, value: formulaValue.trim() };
    } else {
      // Simple case: join values with their operators
      let formulaValue = '';
      values.forEach((value, index) => {
        const quoted = quoteIfNeeded(value);
        if (index === 0) {
          formulaValue = startsWithNegative ? `- ${quoted}` : quoted;
        } else {
          const operatorIndex = startsWithNegative ? index : index - 1;
          const operator = operators[operatorIndex] || '+';
          formulaValue += ` ${operator} ${quoted}`;
        }
      });
      result[key] = { isFormula: true, value: formulaValue };
    }
  });

  const finalResult = !isNil(pageDrivers) ? mergePageDriversToParsedFormula(result, pageDrivers) : result;
  console.debug('[splitRawFormula] finalResult', finalResult);
  return finalResult;
};

/**
 * Converts a per-dimension formula string into an array of KpiFormulaNode objects.
 *
 * Uses a custom tokenizer that walks the string character-by-character, preserving node values verbatim
 * (no numeric normalization). Supports:
 * - Math operators: +, -, *, /
 * - Grouping: (, )
 * - Quoted nodes: "value with spaces" → node with value stripped of quotes
 * - Unquoted nodes: contiguous non-whitespace/non-operator characters
 *
 * @param rawFormula - A per-dimension formula string (e.g., "L60021 + L60020" or '"GP Plus Hedge" / RWACPH')
 */
export const formulaToKpiSegments = (rawFormula: string | null): KpiFormulaNode[] => {
  if (!rawFormula) {
    return [];
  }

  const segments: KpiFormulaNode[] = [];
  let cursor = 0;
  const len = rawFormula.length;

  while (cursor < len) {
    const char = rawFormula[cursor];

    if (char === ' ') {
      // Skip whitespace
      cursor += 1;
    } else if (MATH_OPERATORS.includes(char)) {
      // Math operator
      segments.push({ type: KpiFormulaNodeType.Operator, value: char });
      cursor += 1;
    } else if (char === '(' || char === ')') {
      // Parenthesis (stored as operator type per existing convention)
      segments.push({ type: KpiFormulaNodeType.Operator, value: char });
      cursor += 1;
    } else if (char === '"') {
      // Quoted node — consume until closing quote
      const closeQuote = rawFormula.indexOf('"', cursor + 1);
      if (closeQuote === -1) {
        // Malformed (no closing quote) — treat rest of string as node value
        segments.push({ type: KpiFormulaNodeType.Node, value: rawFormula.substring(cursor + 1) });
        cursor = len;
      } else {
        segments.push({ type: KpiFormulaNodeType.Node, value: rawFormula.substring(cursor + 1, closeQuote) });
        cursor = closeQuote + 1;
      }
    } else {
      // Unquoted node — consume until whitespace, operator, or paren
      let end = cursor;
      while (end < len && !' +-*/()'.includes(rawFormula[end])) {
        end += 1;
      }
      segments.push({ type: KpiFormulaNodeType.Node, value: rawFormula.substring(cursor, end) });
      cursor = end;
    }
  }

  return segments;
};

/**
 * Result of a formula validation check.
 */
export interface FormulaValidationResult {
  isValid: boolean;
  error: string | null;
}

/**
 * Validates that a KPI formula (as segments) represents a mathematically valid expression.
 *
 * Replaces node values with safe placeholder symbols and delegates structural validation to MathJS parse.
 * This avoids MathJS misinterpreting node values (numeric normalization, implicit multiplication)
 * while still leveraging its full grammar validation (balanced parens, operator placement, etc.).
 *
 * @param segments - The KpiFormulaNode array produced by formulaToKpiSegments
 */
export const validateKpiFormula = (segments: KpiFormulaNode[]): FormulaValidationResult => {
  // Filter out blank/Unknown segments (empty input placeholders in the builder UI)
  const meaningfulSegments = segments.filter((s) => s.type !== KpiFormulaNodeType.Unknown && s.value.trim() !== '');

  if (meaningfulSegments.length === 0) {
    return { isValid: false, error: 'Formula is empty.' };
  }

  // A formula must contain at least 2 nodes to remain a formula (otherwise it's a single value)
  const nodeCount = meaningfulSegments.filter((s) => s.type === KpiFormulaNodeType.Node).length;
  if (nodeCount < 2) {
    return { isValid: false, error: 'Formula must contain at least 2 nodes.' };
  }

  // Check leading operator rule: only `-` or `(` are valid as first token
  const firstSegment = meaningfulSegments[0];
  if (firstSegment.type === KpiFormulaNodeType.Operator && firstSegment.value !== '-' && firstSegment.value !== '(') {
    return { isValid: false, error: `Formula cannot begin with operator: ${firstSegment.value}` };
  }

  // Build a sanitized formula string replacing node values with simple symbol placeholders
  let nodeCounter = 0;
  const sanitizedParts: string[] = [];

  meaningfulSegments.forEach((segment) => {
    if (segment.type === KpiFormulaNodeType.Operator) {
      sanitizedParts.push(segment.value);
    } else {
      // Replace each node with a safe alpha symbol (N0, N1, N2, ...)
      sanitizedParts.push(`N${nodeCounter}`);
      nodeCounter += 1;
    }
  });

  const sanitizedFormula = sanitizedParts.join(' ');

  // Use MathJS to validate mathematical structure
  try {
    parse(sanitizedFormula);
    return { isValid: true, error: null };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Invalid formula structure.';
    return { isValid: false, error: message };
  }
};

/**
 * Determines if a formula is a single-dimension formula
 * @param formula
 */
export const isSingleDimensionFormula = (formula: string | ParseResult) => {
  const formulaObject: ParseResult = isString(formula) ? splitRawFormula(formula) : formula;
  // Returns true when only one entry in formulaObject is a formula
  return Object.values(formulaObject).filter((entry) => entry.isFormula).length === 1;
};

/**
 * Determines if a formula contains nested formulas (KPI references within formulas)
 * @param formula - Raw formula string or ParseResult object
 * @returns true if the formula contains |Calc| references indicating nested KPIs
 */
export const hasNestedFormulas = (formula: string | ParseResult): boolean => {
  const formulaObject: ParseResult = isString(formula) ? splitRawFormula(formula) : formula;

  return Object.values(formulaObject).some((entry) => entry.isFormula && entry.value?.includes('|Calc|'));
};

/**
 * Determines if a formula contains dimension values with spaces or math operators.
 * These values cannot be correctly parsed by `formulaToKpiSegments` (which uses mathjs)
 * because mathjs interprets spaces as implicit multiplication and operators as math operations.
 *
 * TODO: Refactor `formulaToKpiSegments` to support dimension values containing spaces or math operators
 *  (e.g., "Units Served - Paid Plus Hedge"). Until then, these formulas are unsupported by the builder.
 *
 * @param formula - Raw formula string or ParseResult object
 * @returns true if any dimension value contains spaces or math operators
 */
export const hasUnsupportedSegmentNames = (formula: string | ParseResult): boolean => {
  // Check individual dimension values within formula segments for spaces or math operators.
  // These cannot be parsed by formulaToKpiSegments (mathjs) which interprets them as math.
  // Must check raw segment values since the ParseResult joins them into a formula string.
  if (!isString(formula)) return false;

  const segmentRegex = /\|([^|]+:[^|]+(?:\|[^|]+:[^|]+)*)\|/g;
  const matches = formula.match(segmentRegex) || [];

  return matches.some((segment) => {
    const inner = segment.slice(1, -1);
    return inner.split('|').some((pair) => {
      const colonIdx = pair.indexOf(':');
      if (colonIdx <= 0) return false;
      const value = pair.substring(colonIdx + 1);
      return /[\s+*/-]/.test(value);
    });
  });
};

/**
 * Determines if a formula is supported by the Calculated KPI Builder
 * Currently: only single-dimension formulas are supported, and no nested formulas/kpi_ids are supported.
 * @param formula
 */
export const isSupportedFormula = (formula: string | ParseResult) => {
  return isSingleDimensionFormula(formula) && !hasNestedFormulas(formula);
};

/**
 * Converts a ParseResult into a `kpi_id` and `kpi_formula` strings
 * @param formulaObject - The ParseResult object to convert
 * @param rootNodes - When provided, removes entries in formulaObject which are not formulas and also refer to rootNode values
 * @param alwaysInclude - Keys to include in the formula even if they match root node values (e.g., for denominator account dimension)
 */
export const generateFormula = (
  formulaObject: ParseResult,
  rootNodes?: RootNodesForDimensionsResponse,
  alwaysInclude?: string[],
): GenerateFormulaResult => {
  console.debug('[generateFormula] formulaObject:', formulaObject);

  if (!isSupportedFormula(formulaObject)) {
    throw new Error('Unsupported Formula. Must be a single-dimension calculation.');
  }

  // Filter out root nodes from non-formula entries
  const filteredFormulaObject: ParseResult = {};

  Object.entries(formulaObject).forEach(([key, entry]) => {
    if (entry.isFormula || alwaysInclude?.includes(key) || !rootNodes) {
      filteredFormulaObject[key] = entry;
    } else {
      const rootNodeData = rootNodes[key];
      console.debug('[generateFormula] rootNodeData:', rootNodeData);
      console.debug('[generateFormula] entry:', entry);
      const isRootNode = rootNodeData?.some((node) => {
        const nodeAttributes =
          typeof node?.attributes === 'string' ? JSON.parse(node.attributes) : node?.attributes || {};
        return entry.value === nodeAttributes?.name;
      });
      if (!isRootNode) {
        filteredFormulaObject[key] = entry;
      }
    }
  });

  // The id begins with the |Calc| prefix
  const idParts = ['Calc'];
  const formulaParts: string[] = [];

  // Generate the ID
  KPI_DIMENSION_RENDER_LIST.forEach((config) => {
    if (config.formulaKey) {
      const entry = filteredFormulaObject[config.key];
      if (entry) {
        // Skip null values
        if (entry.value === null) {
          return;
        }

        // Remove quotes (used for quoting in per-dimension formulas) and blankspace around math operators
        const cleanValue = entry.value?.replace(/"/g, '').replace(/\s*([+\-*/()])\s*/g, '$1');

        if (isEmpty(cleanValue)) {
          return;
        }

        // For SELECT KPI Config Types, we need to skip entries when the value matches the rootValue stored in config.
        if (
          config.baseKpiField.type === BaseKpiDimensionFieldTypes.Select &&
          config.baseKpiField.rootValue === entry.value
        ) {
          return;
        }

        idParts.push(`${config.formulaKey}:${cleanValue}`);
      }
    }
  });

  // Find the formula dimension
  const formulaDimension = Object.entries(filteredFormulaObject).find(([, entry]) => entry.isFormula);
  if (!formulaDimension) {
    throw new Error('No formula dimension found');
  }

  const [formulaDimKey, formulaDimEntry] = formulaDimension;
  const segments = formulaToKpiSegments(formulaDimEntry.value);

  // Build formula parts
  segments.forEach((segment) => {
    if (segment.type === KpiFormulaNodeType.Operator) {
      formulaParts.push(segment.value);
    } else {
      const nodeParts: string[] = [];
      Object.values(KPI_DIMENSION_CONFIG_MAP).forEach((config) => {
        if (config.formulaKey) {
          const entry = filteredFormulaObject[config.key];
          if (entry) {
            // For SELECT KPI Config Types, we need to skip entries when the value matches the rootValue stored in config.
            if (
              config.baseKpiField.type === BaseKpiDimensionFieldTypes.Select &&
              config.baseKpiField.rootValue === entry.value
            ) {
              return;
            }

            const nodeValue = config.key === formulaDimKey ? segment.value : entry.value;

            // Skip null values
            if (nodeValue === null) {
              return;
            }

            nodeParts.push(`${config.formulaKey}:${nodeValue}`);
          }
        }
      });
      formulaParts.push(`|${nodeParts.join('|')}|`);
    }
  });

  // Join formula parts with proper spacing
  let formula = '';
  for (let i = 0; i < formulaParts.length; i += 1) {
    const part = formulaParts[i];
    const prevPart = i > 0 ? formulaParts[i - 1] : '';

    if (i === 0) {
      formula += part;
    } else if (part === ')') {
      formula += part;
    } else if (prevPart === '(') {
      formula += part;
    } else if (prevPart === '-' && i === 1) {
      // Handle unary minus - no space between minus and the first operand
      formula += part;
    } else {
      formula += ` ${part}`;
    }
  }

  // The Result:
  const result: GenerateFormulaResult = {
    id: `|${idParts.join('|')}|`,
    formula,
  };

  return result;
};
